import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { site } from "@/config/site";
import VendraMark from "@/components/VendraMark";

const SHUTTER_EASE = [0.83, 0, 0.17, 1];
const SEEN_KEY = "studio-intro-seen";
const MIN_HOLD = 400;
const HARD_CAP = 1200;

// Curtain-rise, tied to actual readiness: opens as soon as the hero
// texture has decoded, capped at 1200ms, never faster than 400ms. Repeat
// navigations within a session get a 250ms fade instead of the full shutter.
export default function Preloader({ onDone }) {
  const [mode] = useState(() => {
    try {
      return sessionStorage.getItem(SEEN_KEY) ? "repeat" : "first";
    } catch {
      return "first";
    }
  });
  const [open, setOpen] = useState(false);
  const [gone, setGone] = useState(false);

  useEffect(() => {
    try {
      sessionStorage.setItem(SEEN_KEY, "1");
    } catch {
      /* private mode */
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setGone(true);
      onDone();
      return;
    }
    if (mode === "repeat") {
      onDone();
      const t = setTimeout(() => setGone(true), 260);
      return () => clearTimeout(t);
    }
    let cancelled = false;
    const img = new Image();
    img.src = "/media/S13-1600.webp";
    const decoded = img.decode
      ? img.decode().catch(() => {})
      : new Promise((r) => {
          img.onload = r;
          img.onerror = r;
        });
    const cap = new Promise((r) => setTimeout(r, HARD_CAP));
    const minHold = new Promise((r) => setTimeout(r, MIN_HOLD));
    Promise.all([Promise.race([decoded, cap]), minHold]).then(() => {
      if (cancelled) return;
      setOpen(true);
      onDone();
      setTimeout(() => setGone(true), 1250);
    });
    return () => {
      cancelled = true;
    };
  }, [mode, onDone]);

  if (gone) return null;

  if (mode === "repeat") {
    return (
      <motion.div
        className="pointer-events-none fixed inset-0 z-[100] bg-ink"
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        data-testid="preloader"
        aria-hidden="true"
      />
    );
  }

  return (
    <div className="fixed inset-0 z-[100]" data-testid="preloader" aria-hidden="true">
      <motion.div
        className="absolute inset-x-0 top-0 h-1/2 border-b border-gold/25 bg-ink"
        animate={open ? { y: "-100%" } : { y: "0%" }}
        transition={{ duration: 1.1, ease: SHUTTER_EASE, delay: 0.1 }}
      />
      <motion.div
        className="absolute inset-x-0 bottom-0 h-1/2 border-t border-gold/25 bg-ink"
        animate={open ? { y: "100%" } : { y: "0%" }}
        transition={{ duration: 1.1, ease: SHUTTER_EASE, delay: 0.1 }}
      />
      <motion.div
        className="absolute inset-0 flex flex-col items-center justify-center gap-6"
        animate={open ? { opacity: 0, scale: 0.97 } : { opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
      >
        <motion.div
          className="text-brass"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
        >
          <VendraMark tag className="h-24 w-auto sm:h-28" color="currentColor" />
        </motion.div>
        <motion.span
          className="font-mono text-[10px] tracking-[0.34em] text-mutedText"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          A STILL, WAKING
        </motion.span>
      </motion.div>
    </div>
  );
}
